<template>
  <div class="page-container">
    <!-- 顶部控制区域 -->
    <div class="control-panel">
      <h3>通知信息查询</h3>
      
      <!-- 搜索和筛选区域（新增title筛选框） -->
      <div class="search-area">
        <div class="form-group">
          <label>用户ID</label>
          <input 
            type="text" 
            v-model="userId" 
            placeholder="输入用户ID"
          >
        </div>
        <div class="form-group">
          <label>日期</label>
          <input 
            type="date" 
            v-model="selectedDate" 
          >
        </div>
        <div class="form-group">
          <label>标题筛选</label>
          <input 
            type="text" 
            v-model="titleKeyword" 
            placeholder="输入标题关键词"
          >
        </div>
        <div class="form-group">
          <label>Package筛选</label>
          <select
            v-model="selectedPackage"
          >
            <option value="">全部</option>
            <option v-for="pkg in uniquePackages" :key="pkg" :value="pkg">{{ pkg }}</option>
          </select>
        </div>
        <div class="form-group button-group">
          <button @click="fetchNotifications">
            <i class="fa fa-search mr-2"></i>获取通知
          </button>
        </div>
      </div>
      
      <!-- 状态提示（补充筛选条件说明） -->
      <div class="status-message" v-if="loading">
        <i class="fa fa-spinner fa-spin mr-2"></i>正在加载数据...
      </div>
      <div class="status-message error" v-if="errorMessage">
        <i class="fa fa-exclamation-circle mr-2"></i>{{ errorMessage }}
      </div>
      <div v-if="filteredNotifications.length && !loading && !errorMessage" class="status-message success">
        <i class="fa fa-check-circle mr-2"></i>
        共找到 {{ notifications.length }} 条通知，
        <span v-if="titleKeyword || selectedPackage.value">
          按{{ titleKeyword ? '标题关键词"' + titleKeyword + '"' : '' }}
          {{ titleKeyword && selectedPackage.value ? '和' : '' }}
          {{ selectedPackage.value ? 'Package"' + selectedPackage.value + '"' : '' }}筛选后显示 {{ filteredNotifications.length }} 条
        </span>
        <span v-else>未筛选，显示全部 {{ filteredNotifications.length }} 条</span>
      </div>
    </div>
    
    <!-- 通知列表 -->
    <div class="list-container">
      <div class="list-header">
        <div class="list-item">
          <!-- 调整列顺序：日期→标题→时间→内容→Package -->
          <div class="col date-col">日期</div>
          <div class="col title-col">标题</div>
          <div class="col time-col">时间</div>
          <div class="col content-col">内容</div>
          <div class="col package-col">Package</div>
        </div>
      </div>

      <div class="list-body">
        <div
          v-for="(item, index) in filteredNotifications"
          :key="index"
          class="list-item"
        >
          <!-- 日期列：3月19日 星期四 -->
          <div class="col date-col" title="日期: {{ formatDateWithWeek(item.locTime) }}">
            {{ formatDateWithWeek(item.locTime) }}
          </div>
          <!-- 标题列 -->
          <div class="col title-col" title="标题: {{ item.title }}">
            <span v-html="highlightKeyword(item.title, titleKeyword)"></span>
          </div>
          <!-- 时间列：16:34 -->
          <div class="col time-col" title="时间: {{ formatOnlyTime(item.locTime) }}">
            {{ formatOnlyTime(item.locTime) }}
          </div>
          <!-- 内容列 -->
          <div class="col content-col" title="内容: {{ item.content }}">
            {{ item.content }}
          </div>
          <!-- Package列 -->
          <div class="col package-col" title="Package: {{ item.package }}">
            <span :class="getPackageClass(item.package)">{{ item.package }}</span>
          </div>
        </div>
      </div>

      <div class="empty-state" v-if="filteredNotifications.length === 0 && !loading && !errorMessage">
        <span v-if="titleKeyword || selectedPackage.value">
          未找到符合{{ titleKeyword ? '标题关键词"' + titleKeyword + '"' : '' }}
          {{ titleKeyword && selectedPackage.value ? '和' : '' }}
          {{ selectedPackage.value ? 'Package"' + selectedPackage.value + '"' : '' }}的通知数据
        </span>
        <span v-else>暂无通知数据</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import qs from 'qs';

export default {
  name: 'NotifyPage',
  setup() {
    // 数据状态（新增titleKeyword用于标题筛选）
    const userId = ref('72E59F7EF9A1D9DC54664D29E24F6DEA41BCD6DC');
    const selectedDate = ref('');
    const selectedPackage = ref('');
    const titleKeyword = ref(''); // 标题筛选关键词
    const notifications = ref([]);
    const loading = ref(false);
    const errorMessage = ref('');

    // 初始化日期为今天
    onMounted(() => {
      const today = new Date();
      selectedDate.value = today.toISOString().split('T')[0];
    });

    // 原时间格式化方法（保留，备用）
    const formatTime = (timeStr) => {
      if (!timeStr) return '';
      const date = new Date(timeStr);
      return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // 拆分1：仅格式化日期+星期（3月19日 星期四）
    const formatDateWithWeek = (timeStr) => {
      if (!timeStr) return '';
      const date = new Date(timeStr);
      if (date.toString() === 'Invalid Date') return '';

      const weekMap = ['日', '一', '二', '三', '四', '五', '六'];
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const week = weekMap[date.getDay()];

      return `${month}月${day}日 星期${week}`;
    };

    // 拆分2：仅格式化时间（16:34）
    const formatOnlyTime = (timeStr) => {
      if (!timeStr) return '';
      const date = new Date(timeStr);
      if (date.toString() === 'Invalid Date') return '';

      const hour = date.getHours().toString().padStart(2, '0');
      const minute = date.getMinutes().toString().padStart(2, '0');

      return `${hour}:${minute}`;
    };

    // 保留原组合方法（备用）
    const formatTimeWithWeek = (timeStr) => {
      if (!timeStr) return '';
      return `${formatDateWithWeek(timeStr)} ${formatOnlyTime(timeStr)}`;
    };

    // 获取唯一的package列表
    const uniquePackages = computed(() => {
      const packages = new Set();
      notifications.value.forEach(item => {
        if (item.package) packages.add(item.package);
      });
      return Array.from(packages);
    });

    // 筛选通知（叠加title关键词和Package筛选）
    const filteredNotifications = computed(() => {
      return notifications.value.filter(item => {
        // 1. Package筛选：无筛选条件则通过，有则匹配
        const packagePass = !selectedPackage.value || item.package === selectedPackage.value;

        // 2. Title筛选：无关键词则通过，有则模糊匹配（忽略大小写）
        const titlePass = !titleKeyword.value
          ? true
          : item.title?.toLowerCase().includes(titleKeyword.value.toLowerCase());

        // 同时满足两个筛选条件
        return packagePass && titlePass;
      });
    });

    // 高亮标题中的匹配关键词
    const highlightKeyword = (text, keyword) => {
      if (!text || !keyword) return text;
      const reg = new RegExp(keyword, 'gi'); // 全局匹配+忽略大小写
      return text.replace(reg, `<span style="background: #fff3cd; color: #92400e;">$&</span>`);
    };

    // Package样式
    const getPackageClass = (packageName) => {
      const classes = {
        'com.example.app1': 'package-type-1',
        'com.example.app2': 'package-type-2',
        'com.example.app3': 'package-type-3',
        'com.example.app4': 'package-type-4',
      };
      return classes[packageName] || 'package-type-default';
    };

    // 获取通知数据
    const fetchNotifications = async () => {
      if (!userId.value || !selectedDate.value) {
        errorMessage.value = '请输入用户ID和选择日期';
        return;
      }

      notifications.value = [];
      loading.value = true;
      errorMessage.value = '';

      try {
        const response = await axios.post(
          'https://loc.budor.cn/getNotification',
          qs.stringify({ user: userId.value, time: selectedDate.value }),
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        );

        if (response.data?.success) {
          notifications.value = response.data.data || [];
          // 重置筛选条件（保留用户输入的关键词，仅清空Package下拉）
          selectedPackage.value = '';
        } else {
          errorMessage.value = response.data?.msg || '获取通知失败';
        }
      } catch (error) {
        console.error('获取通知错误:', error);
        errorMessage.value = '网络请求错误，请重试';
      } finally {
        loading.value = false;
      }
    };

    return {
      userId,
      selectedDate,
      selectedPackage,
      titleKeyword,
      notifications,
      filteredNotifications,
      uniquePackages,
      loading,
      errorMessage,
      fetchNotifications,
      formatTime,
      formatDateWithWeek, // 暴露日期格式化方法
      formatOnlyTime,     // 暴露时间格式化方法
      formatTimeWithWeek, // 保留原组合方法
      getPackageClass,
      highlightKeyword
    };
  }
};
</script>

<style>
/* 全局样式，不使用scoped确保生效 */
.page-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.control-panel {
  background: white;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.control-panel h3 {
  margin: 0 0 16px 0;
  font-size: 1.2rem;
  font-weight: bold;
}

.search-area {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  flex: 1;
  min-width: 150px; /* 确保小屏幕下不挤压 */
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.875rem;
  color: #4B5563;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #D1D5DB;
  border-radius: 4px;
  font-size: 0.875rem;
}

.button-group {
  display: flex;
  align-items: flex-end;
}

.form-group button {
  padding: 8px 16px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.form-group button:hover {
  background: #2563EB;
}

.status-message {
  margin-top: 8px;
  padding: 8px;
  font-size: 0.875rem;
}

.status-message.error {
  color: #DC2626;
}

.status-message.success {
  color: #10B981;
}

.list-container {
  flex: 1;
  overflow-y: auto;
  background: #F9FAFB;
  padding: 16px;
}

.list-header {
  background: white;
  border-radius: 6px 6px 0 0;
  border-bottom: 1px solid #E5E7EB;
}

.list-item {
  display: flex;
  width: 100%;
}

.list-body .list-item {
  border-bottom: 1px solid #E5E7EB;
  background: white;
}

.list-body .list-item:hover {
  background: #F3F4F6;
}

.col {
  padding: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 重新分配列宽：适配新的5列布局 */
.date-col {
  width: 18%; /* 日期列 */
}

.title-col {
  width: 22%; /* 标题列 */
}

.time-col {
  width: 10%; /* 时间列 */
}

.content-col {
  width: 35%; /* 内容列 */
}

.package-col {
  width: 15%; /* Package列 */
}

.list-header .list-item {
  font-weight: bold;
  font-size: 0.875rem;
  color: #4B5563;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9CA3AF;
}

/* Package类型样式 */
.package-type-1 {
  background: #DBEAFE;
  color: #1E40AF;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
}

.package-type-2 {
  background: #D1FAE5;
  color: #065F46;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
}

.package-type-3 {
  background: #EDE9FE;
  color: #5B21B6;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
}

.package-type-4 {
  background: #FEF3C7;
  color: #92400E;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
}

.package-type-default {
  background: #F3F4F6;
  color: #6B7280;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
}

/* 响应式调整：小屏幕适配 */
@media (max-width: 768px) {
  .col {
    font-size: 0.75rem;
    padding: 8px;
  }
  .search-area {
    gap: 10px;
  }
  /* 小屏幕下调整列宽比例，保证关键信息显示 */
  .date-col { width: 20%; }
  .title-col { width: 25%; }
  .time-col { width: 8%; }
  .content-col { width: 30%; }
  .package-col { width: 17%; }
}
</style>